import React, { useState, useEffect } from "react";
import { Typography, Grid } from "@mui/material";
import { motion, Variants } from "framer-motion";
import { TypingText, TypewriterCursor } from "../../styles/HeroStyles";
import { useTranslation } from "react-i18next";
import fallbackMp4 from "../../assets/video/twist-7-kanonics-live-version.mp4";

const WORD_DURATION = 0.75;
const WORD_SLIDE: Variants = {
  hidden: { y: "110%" },
  visible: (i: number) => ({
    y: 0,
    transition: {
      duration: WORD_DURATION,
      ease: [0.16, 0.77, 0.47, 0.97],
      delay: 0.15 + i * WORD_DURATION,
    },
  }),
};

const makeSubtitleReveal = (wordCount: number): Variants => ({
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.15 + wordCount * WORD_DURATION + 0.1,
    },
  },
});

const Hero: React.FC<{ heroReady?: boolean }> = ({ heroReady = false }) => {
  const { t } = useTranslation();
  const H1_WORDS = t("hero.words", { returnObjects: true }) as string[];
  const TYPEWRITER_TEXT = t("hero.typewriter");
  const SUBTITLE_REVEAL = makeSubtitleReveal(H1_WORDS.length);
  const [useSpline] = useState(() => {
    try {
      const canvas = document.createElement("canvas");
      return !!(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
    } catch {
      return false;
    }
  });

  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [typewriterStarted, setTypewriterStarted] = useState(false);

  useEffect(() => {
    setDisplayText("");
    setIsDeleting(false);
    setTypewriterStarted(false);
  }, [TYPEWRITER_TEXT]);

  useEffect(() => {
    const timer = setTimeout(() => setTypewriterStarted(true), 3200);
    return () => clearTimeout(timer);
  }, [TYPEWRITER_TEXT]);

  useEffect(() => {
    if (!typewriterStarted) return;
    let timeout: ReturnType<typeof setTimeout>;
    if (!isDeleting) {
      if (displayText.length < TYPEWRITER_TEXT.length) {
        timeout = setTimeout(
          () =>
            setDisplayText(TYPEWRITER_TEXT.slice(0, displayText.length + 1)),
          80,
        );
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 2000);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(
          () =>
            setDisplayText(TYPEWRITER_TEXT.slice(0, displayText.length - 1)),
          50,
        );
      } else {
        timeout = setTimeout(() => setIsDeleting(false), 500);
      }
    }
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, typewriterStarted, TYPEWRITER_TEXT]);

  return (
    <>
      <section className="hero-section">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: heroReady ? 1 : 0 }}
          transition={{ duration: 3.5, ease: "easeInOut" }}
          style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}
        >
            {useSpline ? (
              // @ts-ignore
              <spline-viewer
                url="https://prod.spline.design/XLdD30Owp82UCwuk/scene.splinecode"
                style={{ width: "100%", height: "100%", display: "block" }}
              />
            ) : (
              <video
                src={fallbackMp4}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                disablePictureInPicture
                disableRemotePlayback
                {...{ "webkit-playsinline": "true" } as any}
                className="hero-video"
              />
            )}
        </motion.div>

        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "0 24px",
            zIndex: 1,
            position: "relative",
            width: "100%",
          }}
        >
          <Grid container spacing={{ xs: 6, lg: 4 }} alignItems="center">
            <Grid item xs={12} lg={7} sx={{ mt: { lg: -18 } }}>
              <div style={{ marginBottom: 24 }}>
                {H1_WORDS.map((word, i) => (
                  <div
                    key={word}
                    style={{ overflow: "hidden", paddingBottom: "0.25em" }}
                  >
                    <motion.div
                      initial="hidden"
                      animate={heroReady ? "visible" : "hidden"}
                      variants={WORD_SLIDE}
                      custom={i}
                      style={{ willChange: "transform" }}
                    >
                      <Typography
                        variant="h1"
                        sx={{
                          fontSize: { xs: "2.2rem", md: "3.2rem", lg: "4rem" },
                          lineHeight: 1.25,
                          letterSpacing: "-0.025em",
                          fontWeight: 700,
                          fontFamily:
                            '"Segoe UI Semibold", "Segoe UI", sans-serif',
                          display: "block",
                        }}
                      >
                        {word}
                      </Typography>
                    </motion.div>
                  </div>
                ))}
              </div>

              <motion.div
                initial="hidden"
                animate={heroReady ? "visible" : "hidden"}
                variants={SUBTITLE_REVEAL}
              >
                <TypingText
                  sx={{
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    fontWeight: 300,
                    letterSpacing: "0.025em",
                    mb: 5,
                    color: "#1D2F41",
                    fontFamily: '"Calibri", sans-serif',
                  }}
                >
                  {displayText}
                  <TypewriterCursor>_</TypewriterCursor>
                </TypingText>
              </motion.div>
            </Grid>
          </Grid>
        </div>
      </section>
    </>
  );
};

export default Hero;
